// @ts-check
import './App.css';
import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import SeatMapModel from './components/SeatMapModel';
import SeatSelectionComponent from './components/SeatSelectionComponent';
import ViewSeatMapComponent from './components/ViewSeatMapComponent';
import CheckoutComponent from './components/CheckoutComponent';
import EventViewComponent from './components/EventViewComponent';
import EventTicketsComponent from './components/EventTicketsComponent';
import EventResultsPage from './components/EventResultsPage';
import HelpCenter from './components/HelpCenter';
import HomePage from './components/HomePage';
import Datenschutzerklaerung from './components/Datenschutzerklaerung';
import Impressum from './components/Impressum';
import TermsOfUse from './components/TermsOfUse';
import CookiePolicy from './components/CookiePolicy';
import CookieConsent from './components/CookieConsent';
import AppLandingPage from './components/AppLandingPage';
import SiteLayout from './components/SiteLayout';
import LoginScreen from './auth/LoginGate';
import { isLoggedIn } from './auth/auth';
import ReportProfile from './components/ReportProfile';
import DeleteProfile from './components/DeleteProfile';
import TheBowlingClub from './components/TheBowlingClub';
import WirDieBraunschweiger from './components/WirDieBraunschweiger';

// Scrolls to the element referenced by the URL hash (e.g. /hilfe#faq) after a
// route change so the header search suggestions jump to the right section. A
// short retry loop covers the case where the target mounts after navigation.
function ScrollToHash() {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    if (!hash) {
      window.scrollTo(0, 0);
      return;
    }
    const id = decodeURIComponent(hash.slice(1));
    let tries = 0;
    let raf = 0;
    const tryScroll = () => {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }
      if (tries++ < 20) raf = requestAnimationFrame(tryScroll);
    };
    raf = requestAnimationFrame(tryScroll);
    return () => cancelAnimationFrame(raf);
  }, [pathname, hash]);

  return null;
}

// The cookie banner and website login redirect stay out of webview screens
// embedded in the native app, where the app already owns auth/chrome.
const APP_WEBVIEW_ROUTES = ['/checkout', '/selection', '/venue'];

/** @param {{ pathname: string, search: string }} location */
function isAppWebViewRequest(location) {
  const params = new URLSearchParams(location.search);
  return (
    APP_WEBVIEW_ROUTES.includes(location.pathname) ||
    params.get('webview') === '1' ||
    params.get('embed') === '1' ||
    params.get('mode') === 'app'
  );
}

function ChromeCookieConsent() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  if (params.get('cookieSettings') === '1') return <CookieConsent />;
  if (isAppWebViewRequest(location)) return null;
  return <CookieConsent />;
}

/** @param {{ children: React.JSX.Element }} props */
function ProtectedRoute({ children }) {
  const location = useLocation();
  if (isAppWebViewRequest(location)) return children;
  if (isLoggedIn()) return children;
  const redirect = `${location.pathname}${location.search}${location.hash}`;
  return <Navigate to={`/login?redirect=${encodeURIComponent(redirect)}`} replace />;
}

function App() {
  return (
    <Router>
      <ScrollToHash />
      <Routes>
        {/* Login is the only public route without website chrome. */}
        <Route path="/login" element={<LoginScreen />} />

        {/* Public website pages — wrapped in shared header + footer chrome.
            In-app webviews can still hide chrome with ?webview=1 / ?embed=1. */}
        <Route element={<SiteLayout />}>
          <Route path="/" element={<HomePage />} />
          <Route path="/events" element={<EventResultsPage />} />
          <Route path="/event/:slug" element={<EventViewComponent />} />
          <Route path="/event/:slug/tickets" element={<EventTicketsComponent />} />
          <Route path="/event/:slug/:sharingUser" element={<EventViewComponent />} />
          <Route path="/model" element={<SeatMapModel />} />
          <Route path="/selection" element={<SeatSelectionComponent />} />
          <Route path="/venue" element={<ViewSeatMapComponent />} />
          <Route path="/checkout" element={<ProtectedRoute><CheckoutComponent /></ProtectedRoute>} />
          <Route path="/tickets" element={<ProtectedRoute><AppLandingPage variant="tickets" /></ProtectedRoute>} />
          <Route path="/profile" element={<ProtectedRoute><AppLandingPage variant="profile" /></ProtectedRoute>} />
          <Route path="/thebowlingclub" element={<TheBowlingClub />} />
          <Route path="/we-the-people/wir-die-braunschweiger" element={<WirDieBraunschweiger />} />
          <Route path="/wir-die-braunschweiger" element={<WirDieBraunschweiger />} />
          <Route path="/hilfe" element={<HelpCenter />} />
          <Route path="/help" element={<HelpCenter />} />
          <Route path="/datenschutz" element={<Datenschutzerklaerung />} />
          <Route path="/nutzungsbedingungen" element={<TermsOfUse />} />
          <Route path="/profil-melden" element={<ReportProfile />} />
          <Route path="/profil-loeschen" element={<DeleteProfile />} />
          <Route path="/impressum" element={<Impressum />} />
          <Route path="/cookies" element={<CookiePolicy />} />
        </Route>
      </Routes>
      <ChromeCookieConsent />
      <Routes>
        <Route path="/app-datenschutz" element={<Datenschutzerklaerung />} />
        <Route path="/app-impressum" element={<Impressum />} />
         <Route path="/app-nutzungsbedingungen" element={<TermsOfUse />} />
      </Routes>
    </Router>
  );
}

export default App;
